// --- React & Redux
import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
// --- External UI Libraries
import Input from '@dtci/spellbook/Input';
import Select from '@dtci/spellbook/Select';
// --- API
import { datastageApi } from 'datastage/api';
// --- Hooks & Store
import { useDateMathActions } from 'datastage/store/dateMath/useDateMath';
// --- Components
import { InputLabel } from 'datastage/components/InputLabel/InputLabel';
// --- Styles
import '../styles.scss';

// Day type options
const DAY_TYPE_OPTIONS = [
    { value: 'Days', label: 'Days' },
    { value: 'Business Days', label: 'Business Days' },
];

// Helper to check if a value is a meaningful number (not empty, not zero)
const hasValue = (val) => val !== '' && val !== null && val !== undefined && Number(val) !== 0;

/**
 * DateTriggerSection Component
 * Reusable section for Start/End date configuration
 */
function DateTriggerSection({
    title,
    triggers,
    triggerType,
    useExistingKey,
    beforeAfterOptions,
    onInputChange,
}) {
    const useExisting = triggers[useExistingKey];
    const hasTimeValue = hasValue(triggers.years) || hasValue(triggers.months) || hasValue(triggers.weeks) || hasValue(triggers.days);

    return (
        <div className={`macro-section ${useExisting ? 'macro-section--dimmed' : ''}`}>
            <div className="macro-section__header">
                <span>{title}</span>
            </div>

            <div className="segmented-control">
                <button
                    type="button"
                    className={`segmented-control__btn ${useExisting ? 'segmented-control__btn--active' : ''}`}
                    onClick={() => onInputChange(useExistingKey, true, { triggerType })}
                >
                    <i className="fa-solid fa-calendar-check" />
                    Use Existing
                </button>
                <button
                    type="button"
                    className={`segmented-control__btn ${!useExisting ? 'segmented-control__btn--active' : ''}`}
                    onClick={() => onInputChange(useExistingKey, false, { triggerType })}
                >
                    <i className="fa-solid fa-sliders" />
                    Adjust Date Math
                </button>
            </div>

            {!useExisting && (
                <div className="macro-section__content">
                    {/* Time Inputs Grid */}
                    <div className="time-inputs-grid">
                        <div className="time-input-item">
                            <label>Years - before/after trigger</label>
                            <Input
                                value={triggers.years}
                                onChange={e =>
                                    onInputChange('years', e.target.value, { triggerType })
                                }
                                placeholder="0"
                            />
                        </div>
                        <div className="time-input-item">
                            <label>Months - before/after trigger</label>
                            <Input
                                value={triggers.months}
                                onChange={e =>
                                    onInputChange('months', e.target.value, { triggerType })
                                }
                                placeholder="0"
                            />
                        </div>
                        <div className="time-input-item">
                            <label>Weeks - before/after trigger</label>
                            <Input
                                value={triggers.weeks}
                                onChange={e =>
                                    onInputChange('weeks', e.target.value, { triggerType })
                                }
                                placeholder="0"
                            />
                        </div>
                        <div className="time-input-item">
                            <label>Days - before/after trigger</label>
                            <Input
                                value={triggers.days}
                                onChange={e =>
                                    onInputChange('days', e.target.value, { triggerType })
                                }
                                placeholder="0"
                            />
                        </div>
                    </div>

                    {/* Validation Error */}
                    {!hasTimeValue && (
                        <div className="macro-section__error">
                            <i className="fa-solid fa-circle-exclamation" />
                            At least one time value (years, months, weeks, or days) is required
                        </div>
                    )}

                    {/* Options Row */}
                    <div className="macro-options-row">
                        {hasValue(triggers.days) && (
                            <InputLabel
                                label="Day Type"
                                required
                                error={!triggers.dayType && 'Day Type is required when days is specified'}
                            >
                                <Select
                                    isSearchable
                                    options={DAY_TYPE_OPTIONS}
                                    onChange={value =>
                                        onInputChange('dayType', value, { triggerType })
                                    }
                                    value={triggers.dayType}
                                />
                            </InputLabel>
                        )}
                        <InputLabel label="Before / After" required>
                            <Select
                                isSearchable
                                options={beforeAfterOptions}
                                onChange={value =>
                                    onInputChange('beforeAfter', value, { triggerType })
                                }
                                value={triggers.beforeAfter}
                            />
                        </InputLabel>
                    </div>
                </div>
            )}
        </div>
    );
}

/**
 * Macros Form
 *
 * Features:
 * - Renders a form for creating Date Math Macros
 * - Handles input changes and updates Redux state
 *
 * @returns {Element}
 */
export function MacrosForm() {
    const timeoutRef = useRef();
    const debounceTimeout = 500;
    const {
        macroTypes,
        dealSelected,
        selectedMacroType,
        beforeAfterOptions,
        startDateTriggers,
        endDateTriggers,
    } = useSelector((state) => state.dateMath);
    const { updateMacroTriggers, updateDateMath } = useDateMathActions();

    // Handle deal search input changes with debouncing
    const onDealOptionChange = (input, loadOptions) => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(async () => {
            if (input) {
                const data = await datastageApi.deals(input);
                loadOptions(
                    data.map((item) => ({
                        id: item?.id,
                        label: item?.searchableTitle,
                    })),
                );
            }
        }, debounceTimeout);
    };

    const handleInputChange = (field, value, options = {}) => {
        // For dealSelected, updateDateMath
        if (field === 'dealSelected') {
            updateDateMath({ dealSelected: value });
            return;
        }
        // For macro type
        if (field === 'selectedMacroType') {
            updateDateMath({ selectedMacroType: value });
            return;
        }
        // For startDateTriggers and endDateTriggers
        if (options.triggerType) {
            updateMacroTriggers({
                key: options.triggerType,
                data: { [field]: value },
            });
        }
    };

    return (
        <div className="macros-form">
            {/* Top Section: Type and Deal */}
            <div className="macro-form-top">
                <InputLabel label="Macro Type">
                    <Select
                        isSearchable
                        options={macroTypes}
                        value={selectedMacroType}
                        onChange={value => handleInputChange('selectedMacroType', value)}
                    />
                </InputLabel>

                <InputLabel
                    label="Deal"
                    required
                    error={!dealSelected && 'Deal is required'}
                >
                    <Select
                        async
                        loadOptions={onDealOptionChange}
                        isClearable
                        isSearchable
                        onChange={value => handleInputChange('dealSelected', value)}
                        value={dealSelected}
                        placeholder="Search by ID or Title..."
                    />
                </InputLabel>
            </div>

            {/* Main Date Math Section */}
            <div className="macro-main-section">
                <div className="macro-main-section__header">
                    Create date math using existing DRO start/end dates or adjust date math
                </div>

                <div className="macro-main-section__content">
                    {/* Start Date Section */}
                    <DateTriggerSection
                        title="Start Date Configuration"
                        triggers={startDateTriggers}
                        triggerType="startDateTriggers"
                        useExistingKey="useExistingStartDate"
                        beforeAfterOptions={beforeAfterOptions}
                        onInputChange={handleInputChange}
                    />

                    {/* End Date Section */}
                    <DateTriggerSection
                        title="End Date Configuration"
                        triggers={endDateTriggers}
                        triggerType="endDateTriggers"
                        useExistingKey="useExistingEndDate"
                        beforeAfterOptions={beforeAfterOptions}
                        onInputChange={handleInputChange}
                    />
                </div>
            </div>
        </div>
    );
}
